// ─────────────────────────────────────────────────────────────────
// Glass-i — HRMS Organization Chart page (Stage HRMS-3).
// Summary cards + branch / department breakdown + a compact,
// expand/collapse manager hierarchy tree. Read-only
// (hrms.organization.view).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Segmented, Space, Table, Tag, Tree, Spin, Tooltip, message } from 'antd';
import { ReloadOutlined, ApartmentOutlined } from '@ant-design/icons';

import { hrOrganizationApi } from './hrOrganizationApi';
import { forestToTreeData, allKeys, summaryCards } from './organizationHelpers';
import '../hrms.css';

export default function OrganizationChartPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('Hierarchy');
  const [expandedKeys, setExpandedKeys] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await hrOrganizationApi.chart();
      setData(out || null);
      setExpandedKeys(allKeys((out && out.managerHierarchy) || []));
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load organization chart.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cards = summaryCards(data && data.summary);

  const renderTitle = (node) => (
    <span>
      <strong>{node.employeeName}</strong>
      <span style={{ color: '#94a3b8' }}> · {node.employeeCode}</span>
      {node.designationName ? <span style={{ color: '#64748b' }}> — {node.designationName}</span> : null}
      {node.branchName ? <Tag style={{ marginLeft: 6 }}>{node.branchName}</Tag> : null}
      {!node.active ? <Tag color="default" style={{ marginLeft: 4 }}>{node.status}</Tag> : null}
      {node.isSalesperson ? <Tag color="blue" style={{ marginLeft: 4 }}>SALES</Tag> : null}
      {node.directReports ? <Tag color="geekblue" style={{ marginLeft: 4 }}>{node.directReports} reports</Tag> : null}
    </span>
  );

  const treeData = useMemo(
    () => forestToTreeData((data && data.managerHierarchy) || [], renderTitle),
    [data]);

  const breakdownColumns = [
    { title: 'Name', dataIndex: 'name', render: (v) => v || '—' },
    { title: 'Employees', dataIndex: 'employeeCount', width: 120, align: 'right' },
    { title: 'Active', dataIndex: 'activeCount', width: 100, align: 'right' },
    { title: 'Salespersons', dataIndex: 'salespersonCount', width: 130, align: 'right' }
  ];

  return (
    <div className="hr-shell">
      <div className="hr-headstrip">
        <div className="hr-headstrip__lead">
          <span className="hr-headstrip__icon"><ApartmentOutlined /></span>
          <div className="hr-headstrip__text">
            <span className="hr-headstrip__title">Organization Chart</span>
            <span className="hr-headstrip__subtitle">Branch, department & manager hierarchy with live head-counts.</span>
          </div>
        </div>
        <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
      </div>

      <Spin spinning={loading}>
        <div className="hr-org-cards">
          {cards.map((c) => (
            <Card key={c.key} size="small" className="hr-org-card">
              <div className="hr-org-card__value">{c.value}</div>
              <div className="hr-org-card__label">{c.label}</div>
            </Card>
          ))}
        </div>

        <div className="hr-toolbar" style={{ marginTop: 12 }}>
          <Segmented size="small" value={view} onChange={setView}
            options={['Hierarchy', 'By Branch', 'By Department']} />
          <div className="hr-toolbar__spacer" />
          {view === 'Hierarchy' ? (
            <Space size={6}>
              <Button size="small" onClick={() => setExpandedKeys(allKeys((data && data.managerHierarchy) || []))}>Expand all</Button>
              <Button size="small" onClick={() => setExpandedKeys([])}>Collapse all</Button>
            </Space>
          ) : null}
        </div>

        <div className="hr-card">
          {view === 'Hierarchy' ? (
            treeData.length ? (
              <Tree className="hr-org-tree" treeData={treeData} showLine selectable={false}
                expandedKeys={expandedKeys} onExpand={setExpandedKeys} />
            ) : <div className="hr-empty">No employees to chart yet.</div>
          ) : null}
          {view === 'By Branch' ? (
            <Table className="hr-grid" size="small" rowKey="id" pagination={false}
              dataSource={(data && data.branches) || []} columns={breakdownColumns}
              locale={{ emptyText: 'No branch assignments yet.' }} />
          ) : null}
          {view === 'By Department' ? (
            <Table className="hr-grid" size="small" rowKey="id" pagination={false}
              dataSource={(data && data.departments) || []} columns={breakdownColumns}
              locale={{ emptyText: 'No department assignments yet.' }} />
          ) : null}
        </div>
      </Spin>
    </div>
  );
}
